
import Vue, { PropType } from 'vue'
import VisitHistoryCard from '@/components/chisai/GCB2/clientInfo/VisitHistoryCard.vue'
import { getClientVisitsHistoryData } from '@/api/chisai/GCB2'
import { CallbotTaskAttempt, getCallbotTask } from '@/api/callbot'
import { CallbotTask, CallbotTaskAction } from '@/types/callbot'
import { attemptResultLabel, formatTaskPhone, taskStatusLabel } from './taskFormat'

export default Vue.extend({
    name: 'CallbotClientSidebar',
    components: { VisitHistoryCard },
    props: {
        value: { type: Boolean, default: false },
        projectId: { type: String, required: true },
        task: { type: Object as PropType<CallbotTask | null>, default: null },
        canManage: { type: Boolean, default: false },
        busy: { type: Boolean, default: false },
    },
    data() {
        return {
            attempts: [] as CallbotTaskAttempt[],
            detailLoading: false,
            visits: [] as unknown[],
            visitsLoading: false,
            visitsError: false,
            copied: false,
            // Гасим гонки при быстром переключении между лидами.
            requestId: 0,
            debounce: 0 as unknown as ReturnType<typeof setTimeout>,
        }
    },
    computed: {
        groupLabel(): string {
            if (!this.task) return ''
            return (
                {
                    clarify: 'Уточните статус',
                    deferred: 'Отложенная коммуникация',
                    recorded: 'Записаны',
                    do_not_call: 'Не звонить',
                }[this.task.group] || this.task.group
            )
        },
        overdue(): boolean {
            if (!this.task || !this.task.dueAt) return false
            return new Date(this.task.dueAt).getTime() < Date.now()
        },
        whenLabel(): string {
            if (!this.task) return ''
            const value =
                this.task.dueAt || this.task.callFinishedAt || this.task.updatedAt
            return value ? this.formatDateTime(value) : '—'
        },
        actions(): Array<{ status: CallbotTaskAction; label: string; className: string }> {
            return [
                { status: 'recorded', label: 'Записан', className: 'task-success' },
                { status: 'deferred', label: 'Отложить', className: '' },
                { status: 'not_recorded', label: 'Не записан', className: '' },
                { status: 'do_not_call', label: 'Не звонить', className: 'task-danger' },
            ]
        },
    },
    watch: {
        task: {
            immediate: true,
            handler() {
                clearTimeout(this.debounce)
                this.copied = false
                if (!this.value || !this.task) return
                // sidebar_visit_history — тяжёлая функция на основной базе,
                // при быстром переключении лидов без задержки будет шквал.
                this.debounce = setTimeout(() => this.reload(), 200)
            },
        },
        value(opened: boolean) {
            if (opened) this.reload()
            else clearTimeout(this.debounce)
        },
    },
    mounted() {
        window.addEventListener('keydown', this.onKeydown)
    },
    beforeDestroy() {
        window.removeEventListener('keydown', this.onKeydown)
        clearTimeout(this.debounce)
    },
    methods: {
        formatTaskPhone,
        taskStatusLabel,
        attemptResultLabel,
        onKeydown(event: KeyboardEvent) {
            if (event.key === 'Escape' && this.value) this.close()
        },
        close() {
            this.$emit('input', false)
        },
        formatDateTime(value: string): string {
            const date = new Date(value)
            if (Number.isNaN(date.getTime())) return '—'
            return date.toLocaleString('ru-RU', {
                day: 'numeric',
                month: 'long',
                hour: '2-digit',
                minute: '2-digit',
            })
        },
        async copyPhone() {
            if (!this.task) return
            try {
                await navigator.clipboard.writeText(this.task.phone)
            } catch {
                const area = document.createElement('textarea')
                area.value = this.task.phone
                document.body.appendChild(area)
                area.select()
                document.execCommand('copy')
                document.body.removeChild(area)
            }
            this.copied = true
            setTimeout(() => {
                this.copied = false
            }, 1500)
        },
        async reload() {
            if (!this.task) return
            const generation = ++this.requestId
            this.attempts = []
            this.detailLoading = true
            const detail = await getCallbotTask(this.projectId, this.task.taskId)
            if (generation !== this.requestId) return
            this.detailLoading = false
            if (detail.data) this.attempts = detail.data.attempts
            await this.loadVisits(generation)
        },
        async loadVisits(generation?: number) {
            const stamp = generation ?? this.requestId
            if (!this.task || !this.task.clientId) return
            this.visitsError = false
            this.visitsLoading = true
            this.visits = []
            const response = await getClientVisitsHistoryData({
                projectId: this.projectId,
                clientId: this.task.clientId,
            })
            if (stamp !== this.requestId) return
            this.visitsLoading = false
            if (response.error) {
                this.visitsError = true
                return
            }
            this.visits = (response.data as unknown[]) || []
        },
    },
})
